/**
 * MCP Tool Config Tab
 *
 * Renders the configuration surface for an MCP tool selected on the canvas.
 * Surfaces the bound connection reference and the allowed-tools allow-list
 * fetched lazily from the runtime via `listMcpTools`.
 *
 * Supports two tool kinds:
 *  - BYO (custom endpoint): the connection already exists in `agentMcpConnections`;
 *    `inputs.connectionReference.connectionName` is its record key.
 *  - Managed (Microsoft / custom connector): added to canvas WITHOUT a
 *    connection (mirroring Otto's managed-API flow). Identified by
 *    `toolData.kind === 'Managed'` plus a connector id on
 *    `inputs.connectionReference.api.id` and a resolved swagger path on
 *    `inputs.parameters.mcpServerPath`. When no `connectionName` is bound yet,
 *    this tab renders an existing-connections picker (same pattern as
 *    `ConnectionTab` for standard actions): pre-existing connections for the
 *    same connector are listed and bindable in one click; "Create new
 *    connection" toggles to `ConnectionCreationPanel`. On bind (existing OR
 *    newly created) the reference is registered with the connection store, its
 *    key is written onto the tool via `setToolMcpConnectionName`, and managed
 *    `listMcpTools` (which needs `connectionRuntimeUrl` + `authentication` +
 *    `mcpServerPath`) populates the allowed-tools picker. Picking an existing
 *    connection that is not yet in the store fetches its detail via
 *    `getConnection` to materialize the full `ConnectionReference` (including
 *    `connectionRuntimeUrl`), since the list response often omits it.
 *
 * Runtime contract:
 *  - `inputs.connectionReference.connectionName` is the connection key the
 *    runtime resolves at execution time (BYO: an `agentMcpConnections` record key;
 *    Managed: a `connections.json` reference key).
 *  - Omitting `inputs.parameters.allowedTools` means "all tools allowed".
 *    Persisting an explicit array (possibly empty) means "exactly these".
 *    Use the store setter `setToolMcpAllowedTools(agentName, toolName,
 *    allowedTools | undefined)` so the empty-array vs. undefined distinction
 *    is preserved (deep merge over `inputs.parameters`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Text,
  Checkbox,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Button,
  Field,
  Caption1,
  Input,
} from '@fluentui/react-components';
import {
  Add16Regular,
  ArrowClockwiseRegular,
  ChevronRightRegular,
  PlugConnectedRegular,
  Search16Regular,
} from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import type { AgentTool } from '@/types/workflowDefinition';
import { isAgentAction } from '@/types/workflowDefinition';
import type { ConnectionReference } from '@/types/connectionReference';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useMcpConnectionsForApp, useMcpToolsForConnection } from '@/hooks/useMcpServers';
import {
  useAppContextBuilder,
  useConnections,
  useRegisterCreatedConnection,
} from '@/hooks/useConnectionMutations';
import { mcpConnectionToInfo, managedReferenceToMcpConnectionInfo } from '@/services/mcpHelpers';
import { getConnection, toConnectionReference } from '@/services/connectionApi';
import { ConnectionCreationPanel } from '@/components/WorkflowDesigner/NodeEditor/dialogs/ConnectionCreationPanel';
import { CompactConnectionItem } from '@/components/common/CompactConnectionItem';
import { extractConnectorName } from '@/utils/connections';
import { sanitizeErrorMessage } from '@/utils/sanitization';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
  },
  connectionValue: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground2,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
  },
  toolsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    maxHeight: '320px',
    overflowY: 'auto',
  },
  toolRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusSmall,
  },
  toolDescription: {
    marginLeft: '28px',
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  inlineActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  // Existing-connections picker
  pickerHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  pickerHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  pickerSubtitle: {
    color: tokens.colorNeutralForeground3,
  },
  createNewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      border: `1px dashed ${tokens.colorBrandStroke1}`,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  createNewIcon: {
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  createNewText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  pickerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    maxHeight: '320px',
    overflowY: 'auto',
  },
  pickerEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    gap: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
  // "Connection" banner used on the bound managed view — mirrors
  // ConnectionErrorBanner's connected variant so MCP visually matches the
  // existing API action panel pattern (plug icon + CONNECTION label + name +
  // Manage button).
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
  },
  bannerConnected: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  bannerMissing: {
    backgroundColor: tokens.colorStatusWarningBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorStatusWarningBorder1),
  },
  bannerIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  iconConnected: {
    color: tokens.colorPaletteGreenForeground1,
  },
  iconMissing: {
    color: tokens.colorStatusWarningForeground1,
  },
  bannerContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: '2px',
  },
  bannerLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  bannerName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bannerManage: {
    flexShrink: 0,
  },
  reconnectHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
});

interface McpToolConfigTabProps {
  agentName: string;
  toolName: string;
  toolData: AgentTool;
}

interface ToolDescriptor {
  name: string;
  description?: string;
}

function extractToolList(raw: unknown): ToolDescriptor[] {
  // useMcpToolsForConnection returns McpToolFromBackend[] directly, but stay
  // permissive in case the runtime envelope leaks through.
  const candidates: unknown = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? ((raw as Record<string, unknown>)['tools'] ??
        (raw as Record<string, unknown>)['result'] ??
        (raw as Record<string, unknown>)['value'])
      : null;
  if (!Array.isArray(candidates)) return [];
  const out: ToolDescriptor[] = [];
  for (const item of candidates) {
    if (item && typeof item === 'object') {
      const t = item as Record<string, unknown>;
      const name = (t['name'] ?? t['id']) as string | undefined;
      if (typeof name === 'string' && name.length > 0) {
        const desc = t['description'];
        out.push({
          name,
          ...(typeof desc === 'string' && desc.length > 0 ? { description: desc } : {}),
        });
      }
    }
  }
  return out;
}

export const McpToolConfigTab: React.FC<McpToolConfigTabProps> = ({
  agentName,
  toolName,
  toolData,
}) => {
  const styles = useStyles();
  const setToolMcpAllowedTools = useWorkflowStore((s) => s.setToolMcpAllowedTools);
  const setToolMcpConnectionName = useWorkflowStore((s) => s.setToolMcpConnectionName);
  const registerCreatedConnection = useRegisterCreatedConnection();
  const queryClient = useQueryClient();
  const { workflowName } = useParams<{ workflowName: string }>();

  // Live source of truth. `toolData` is captured as a snapshot at
  // `openNodeEditor` time inside `nodeEditorStore`, so it does NOT update when
  // the workflow store mutates (e.g. when we rebind the connection here).
  // Subscribe directly to the workflow store so any change to this tool
  // (connectionName, allowed tools, etc.) re-renders the tab immediately.
  // Fall back to the snapshot prop if the workflow store hasn't loaded the
  // tool yet (defensive — keeps tests + the very first render safe).
  const liveToolData = useWorkflowStore((s) => {
    const agent = s.workflow?.definition.actions[agentName];
    if (!agent || !isAgentAction(agent)) return undefined;
    return agent.tools?.[toolName];
  });
  const effectiveTool: AgentTool = liveToolData ?? toolData;

  const inputs = (effectiveTool.inputs ?? {}) as Record<string, unknown>;
  const connectionRef = (inputs['connectionReference'] ?? {}) as {
    connectionName?: string;
    api?: { id?: string };
  };
  const params = (inputs['parameters'] ?? {}) as Record<string, unknown>;
  const connectionKey = connectionRef.connectionName ?? '';
  const allowedTools = Array.isArray(params['allowedTools'])
    ? (params['allowedTools'] as string[])
    : undefined;

  // Managed (Microsoft) MCP tools carry a managed-API connector id on
  // `connectionReference.api.id` and a resolved swagger path on
  // `parameters.mcpServerPath`. BYO tools have neither. A managed tool may be
  // added to canvas WITHOUT a connection (mirroring Otto's managed-API flow);
  // the connection is created here in the node editor.
  const apiId = connectionRef.api?.id;
  const isManaged =
    effectiveTool.kind === 'Managed' && typeof apiId === 'string' && apiId.length > 0;
  const connectorId = isManaged ? apiId : undefined;
  const mcpServerPath =
    typeof params['mcpServerPath'] === 'string' && params['mcpServerPath'].length > 0
      ? (params['mcpServerPath'] as string)
      : undefined;

  // Skip BYO connection subscriptions for managed tools — their records are
  // never read below (see the `!isManaged && connectionKey` guard further
  // down), so the underlying connections.json + draft React Query
  // subscriptions are pure overhead in the managed case.
  const { records: mcpConnections } = useMcpConnectionsForApp({ enabled: !isManaged });

  // Track a connection created in this session. Tag it with the tool identity
  // (agent + tool) AND the key so a stale create from a previously-selected
  // tool can never leak — the reset effect below runs AFTER render, so on a
  // Tool1→Tool2 switch the old `created` is still present during Tool2's first
  // render; scoping by identity makes that one-render window inert.
  const [created, setCreated] = useState<{
    agentName: string;
    toolName: string;
    refKey: string;
    ref: ConnectionReference;
  } | null>(null);
  // The connection list is always visible on the bound view (mirroring
  // ConnectionTab's pattern — no separate "Change connection" toggle); clicking
  // "Create new connection" replaces the picker with the creation panel.
  const [showCreationPanel, setShowCreationPanel] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [pickingId, setPickingId] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  // Managed bound view starts as a compact "CONNECTION" banner (mirroring the
  // managed-API panel pattern). Clicking "Manage" flips the panel into the
  // picker view so the user can choose another existing connection or create
  // a new one; Cancel returns to the banner. Always reset when switching tools
  // (the reset effect below) and on successful (re-)bind.
  const [reconnecting, setReconnecting] = useState(false);
  useEffect(() => {
    setCreated(null);
    setShowCreationPanel(false);
    setPickerError(null);
    setPickingId(null);
    setPickerSearch('');
    setReconnecting(false);
  }, [agentName, toolName, connectionKey]);

  // Only honor `created` when it belongs to the currently-rendered tool.
  const createdForThisTool =
    created && created.agentName === agentName && created.toolName === toolName ? created : null;

  // Narrow conditional selector — only subscribe to the one reference we care
  // about, and only when a key exists (avoids re-render churn on unrelated refs).
  const managedReference = useConnectionStore((s) =>
    connectionKey ? s.references[connectionKey] : undefined,
  );
  // Picker only: subscribe to the whole references map so newly-registered
  // connections (created in another tab) show up immediately. Cheap because
  // the map object identity is stable unless `addConnectionReference` ran.
  const allReferences = useConnectionStore((s) => s.references);

  // Imperative service-call context for fetching connection detail when we
  // bind an existing connection that isn't already in the store. Always
  // called (hook ordering); guarded by isManaged inside the handler.
  const { buildContext } = useAppContextBuilder();
  // Always call (hook ordering); gated server-side via `enabled`.
  const availableConnections = useConnections(
    undefined,
    connectorId,
    isManaged && !!connectorId,
  );

  const connectionRecord = !isManaged && connectionKey ? mcpConnections[connectionKey] : undefined;
  const displayName = connectionRecord?.displayName ?? connectionKey;

  const effectiveKey = connectionKey || createdForThisTool?.refKey || '';
  // Use the just-created reference only when it matches the effective key, so a
  // stale `created` (e.g. mid-effect-reset) can never be read for another tool.
  const effectiveManagedRef =
    createdForThisTool && createdForThisTool.refKey === effectiveKey
      ? createdForThisTool.ref
      : managedReference;

  const connectionInfo = useMemo(
    () =>
      isManaged
        ? managedReferenceToMcpConnectionInfo(effectiveManagedRef, effectiveKey)
        : connectionRecord
          ? mcpConnectionToInfo(connectionRecord, connectionKey)
          : undefined,
    [isManaged, effectiveManagedRef, effectiveKey, connectionRecord, connectionKey],
  );

  // Managed list-tools requires a resolved mcpServerPath; gate the query on it
  // so we never fire a guaranteed-throw managed body.
  const toolsEnabled = isManaged ? !!connectionInfo?.id && !!mcpServerPath : true;

  const {
    data: toolsResponse,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useMcpToolsForConnection(
    connectionInfo,
    workflowName,
    isManaged ? mcpServerPath : undefined,
    toolsEnabled,
  );

  const tools = useMemo(() => extractToolList(toolsResponse), [toolsResponse]);

  const isAllowed = (name: string) => {
    // Omitted allowedTools means "all allowed".
    if (allowedTools === undefined) return true;
    return allowedTools.includes(name);
  };

  const toggleTool = (name: string, next: boolean) => {
    // Build the new explicit list from the discovered tools. We intentionally
    // do NOT preserve unknown names: if the server no longer offers a tool,
    // it's silently dropped from the allow-list on the next toggle.
    const current = allowedTools ?? tools.map((t) => t.name);
    let updated: string[];
    if (next) {
      updated = current.includes(name) ? current : [...current, name];
    } else {
      updated = current.filter((n) => n !== name);
    }
    setToolMcpAllowedTools(agentName, toolName, updated);
  };

  const handleAllowAll = () => {
    // Omit the key to express "all tools allowed" — runtime semantic.
    setToolMcpAllowedTools(agentName, toolName, undefined);
  };

  const handleRetry = async () => {
    await queryClient.invalidateQueries({ queryKey: ['mcp', 'connectionTools'] });
    await refetch();
  };

  const handleManagedCreateComplete = (refKey: string, ref: ConnectionReference) => {
    // Mirror ConnectionTab.handleCreateComplete: register the reference (so the
    // store + connections.json reflect it), bind the key onto the tool, then
    // record it locally so list-tools can fire immediately.
    registerCreatedConnection(refKey, ref);
    setToolMcpConnectionName(agentName, toolName, refKey);
    setCreated({ agentName, toolName, refKey, ref });
    setShowCreationPanel(false);
    // Flip out of picker/creation back to the banner now that the rebind is
    // done. The reset effect will also clear this when connectionKey catches
    // up via the store; this avoids an awkward intermediate render where
    // effectiveKey is set but the picker is still showing.
    setReconnecting(false);
  };

  // Bound-view "Manage" affordance: flip into the picker takeover. Reset stale
  // picker state first so a previous error message or in-flight pick from an
  // earlier session doesn't leak into the new picker view.
  const handleManageConnection = () => {
    setPickerError(null);
    setPickingId(null);
    setPickerSearch('');
    setReconnecting(true);
  };

  const handleCancelReconnect = () => {
    setReconnecting(false);
    setPickerError(null);
    setPickerSearch('');
  };

  // Existing-connections picker rows. Merges:
  //  - data-plane results from listConnections (filtered server-side by
  //    `properties/api/id eq '...'`).
  //  - any store references whose `api.id` matches `connectorId` that the list
  //    response doesn't cover (e.g. a connection just registered locally).
  // Connector name comparison uses the short connector segment so swagger-id
  // vs api-id format mismatches don't drop valid rows.
  const mcpExistingConnections = useMemo(() => {
    if (!isManaged || !connectorId) return [];
    const targetConnector = extractConnectorName(connectorId).toLowerCase();
    const data = availableConnections.data ?? [];
    const availableIds = new Set(
      data
        .map((c) => c.id?.toLowerCase())
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    );

    const rows: { key: string; name: string }[] = [];
    for (const conn of data) {
      const connectorName = extractConnectorName(conn.connectorId ?? '').toLowerCase();
      if (connectorName && connectorName !== targetConnector) continue;
      rows.push({ key: conn.name, name: conn.displayName ?? conn.name });
    }
    // Merge in any store-only refs not represented by the data-plane list.
    for (const [refKey, ref] of Object.entries(allReferences)) {
      if (!ref) continue;
      const refConnector = extractConnectorName(ref.api?.id ?? '').toLowerCase();
      if (!refConnector || refConnector !== targetConnector) continue;
      const backingId = ref.connection?.id?.toLowerCase();
      if (backingId && availableIds.has(backingId)) continue;
      // Skip rows already present by key (avoid duplicate render).
      if (rows.some((r) => r.key === refKey)) continue;
      rows.push({ key: refKey, name: refKey });
    }

    const q = pickerSearch.trim().toLowerCase();
    return q
      ? rows.filter(
          (r) => r.name.toLowerCase().includes(q) || r.key.toLowerCase().includes(q),
        )
      : rows;
  }, [isManaged, connectorId, availableConnections.data, allReferences, pickerSearch]);

  // Friendly display name for the managed bound-view banner. Falls back to the
  // reference key when no display name is known (e.g. the connection list
  // hasn't loaded yet or the connection lives only in the reference map).
  // Hoisted above the conditional returns to satisfy the rules of hooks.
  const managedDisplayName = useMemo(() => {
    if (!isManaged || !effectiveKey) return '';
    const match = (availableConnections.data ?? []).find((c) => c.name === effectiveKey);
    return match?.displayName ?? match?.name ?? effectiveKey;
  }, [isManaged, effectiveKey, availableConnections.data]);

  const handleSelectExisting = useCallback(
    async (refKey: string) => {
      if (!isManaged) return;
      // Re-entrancy guard: if a pick is already in flight, ignore further
      // clicks even on the same row. The row stays visually disabled via
      // `pickingId !== null` (below) but this protects against the brief
      // pre-render window or any path that bypasses the disabled prop.
      if (pickingId !== null) return;
      setPickerError(null);
      setPickingId(refKey);
      try {
        // Fast path: the store already has a full ConnectionReference for this
        // key (e.g. we just registered it locally, or another tab fetched it).
        // Validate the cached ref actually belongs to this connector before
        // trusting it — a stale ref with a colliding key for a different
        // connector would otherwise bind incorrectly and break listMcpTools.
        const existing = allReferences[refKey];
        const existingConnectorMatches =
          !!existing &&
          extractConnectorName(existing.api?.id ?? '').toLowerCase() ===
            (connectorId ? extractConnectorName(connectorId).toLowerCase() : '');
        if (existing && existingConnectorMatches) {
          setToolMcpConnectionName(agentName, toolName, refKey);
          setCreated({ agentName, toolName, refKey, ref: existing });
          setShowCreationPanel(false);
          setReconnecting(false);
          return;
        }
        // Slow path: fetch the full connection detail (the list response often
        // omits `connectionRuntimeUrl`, which managed listMcpTools requires),
        // convert via the same helper consumed elsewhere, then register + bind.
        const ctx = await buildContext();
        if (!ctx) {
          setPickerError(
            'Could not load connection details. Sign in to this application and try again.',
          );
          return;
        }
        const resource = await getConnection(ctx, refKey);
        const ref = toConnectionReference(resource);
        registerCreatedConnection(refKey, ref);
        setToolMcpConnectionName(agentName, toolName, refKey);
        setCreated({ agentName, toolName, refKey, ref });
        setShowCreationPanel(false);
        setReconnecting(false);
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setPickerError(`Could not load connection details: ${sanitizeErrorMessage(message)}`);
      } finally {
        setPickingId(null);
      }
    },
    [
      isManaged,
      pickingId,
      allReferences,
      connectorId,
      buildContext,
      registerCreatedConnection,
      setToolMcpConnectionName,
      agentName,
      toolName,
    ],
  );

  // For managed tools, surface a friendly reason the allowed-tools picker can't
  // load yet (rather than firing a guaranteed-fail request). Derive from
  // `connectionInfo` so an incomplete reference (present but missing a runtime
  // URL) also routes to the recovery path instead of a misleading empty state.
  const managedBlockReason: 'missing-ref' | 'missing-path' | null = !isManaged
    ? null
    : effectiveKey && !connectionInfo
      ? 'missing-ref'
      : effectiveKey && connectionInfo && !mcpServerPath
        ? 'missing-path'
        : null;

  const errorMessage = error instanceof Error ? error.message : null;
  const is403 = !!errorMessage && /\b403\b/.test(errorMessage);

  // Shared connection-picker block used by:
  //  - the unbound managed takeover view (full-panel; primary affordance), and
  //  - the bound managed "reconnecting" takeover view (after clicking Manage).
  // Both surfaces are full-panel so a single render output is correct.
  const renderConnectionPicker = () => {
    const listError = availableConnections.error;
    const listErrorMessage = listError instanceof Error ? listError.message : null;
    const isListLoading = availableConnections.isLoading || availableConnections.isFetching;
    const rows = mcpExistingConnections;
    return (
      <>
        <div className={styles.pickerHeader}>
          <Text weight="semibold">Select a connection</Text>
          <Text size={200} className={styles.pickerSubtitle}>
            Pick an existing connection for this MCP server, or create a new one.
          </Text>
          <Input
            value={pickerSearch}
            onChange={(_e, data) => setPickerSearch(data.value)}
            placeholder="Search connections"
            contentBefore={<Search16Regular />}
            aria-label="Search connections"
            size="small"
          />
        </div>

        <div
          className={styles.createNewButton}
          role="button"
          tabIndex={0}
          onClick={() => setShowCreationPanel(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowCreationPanel(true);
            }
          }}
        >
          <div className={styles.createNewIcon}>
            <Add16Regular />
          </div>
          <Text className={styles.createNewText}>Create new connection</Text>
        </div>

        {pickerError ? (
          <MessageBar intent="error">
            <MessageBarBody>{pickerError}</MessageBarBody>
          </MessageBar>
        ) : null}

        {listErrorMessage ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              Could not load existing connections: {sanitizeErrorMessage(listErrorMessage)}. Use "Create new
              connection" to continue.
            </MessageBarBody>
          </MessageBar>
        ) : null}

        {isListLoading ? (
          <div className={styles.pickerEmpty}>
            <Spinner size="tiny" label="Loading connections..." />
          </div>
        ) : rows.length === 0 ? (
          <div className={styles.pickerEmpty}>
            <Text size={200}>No connections for this connector yet. Create one to continue.</Text>
          </div>
        ) : (
          <div
            className={styles.pickerList}
            role="listbox"
            aria-label="Available connections"
          >
            {rows.map((row) => (
              <CompactConnectionItem
                key={row.key}
                name={row.name}
                referenceKey={row.key}
                isSelected={row.key === effectiveKey}
                disabled={pickingId !== null}
                onSelect={(refKey) => void handleSelectExisting(refKey)}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  // Managed tool with no bound connection: full-panel takeover with the picker
  // as the primary surface and a creation panel reachable via "Create new".
  if (isManaged && !effectiveKey) {
    if (showCreationPanel) {
      return (
        <div className={styles.root}>
          <ConnectionCreationPanel
            {...(connectorId ? { connectorId } : {})}
            onComplete={handleManagedCreateComplete}
            onBack={() => setShowCreationPanel(false)}
          />
        </div>
      );
    }
    return <div className={styles.root}>{renderConnectionPicker()}</div>;
  }

  // Managed bound view may still flip into the creation panel from the
  // "Create new connection" affordance inside the reconnecting picker takeover.
  if (isManaged && showCreationPanel) {
    return (
      <div className={styles.root}>
        <ConnectionCreationPanel
          {...(connectorId ? { connectorId } : {})}
          onComplete={handleManagedCreateComplete}
          onBack={() => setShowCreationPanel(false)}
        />
      </div>
    );
  }

  // Managed bound view, user clicked Manage on the banner → full-panel picker
  // takeover with Cancel + (path warning if applicable) above the picker.
  // Mirrors ConnectionErrorBanner -> ConnectionTab navigation in the
  // managed-API panel, adapted for MCP's single-tab editor.
  if (isManaged && reconnecting) {
    return (
      <div className={styles.root}>
        <div className={styles.reconnectHeader}>
          <Button appearance="subtle" size="small" onClick={handleCancelReconnect}>
            Cancel
          </Button>
        </div>
        {managedBlockReason === 'missing-path' && (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Server path unavailable</MessageBarTitle>
              We couldn't resolve the MCP server path for this tool. Picking a
              different connection won't change this, but you can still rebind
              if needed.
            </MessageBarBody>
          </MessageBar>
        )}
        {renderConnectionPicker()}
      </div>
    );
  }

  const hasConnection = isManaged ? !!effectiveKey : !!connectionKey;
  const connectionLabel = isManaged ? effectiveKey : displayName;
  const connectionEndpoint = isManaged
    ? effectiveManagedRef?.connectionRuntimeUrl
    : connectionRecord?.mcpServerUrl;

  return (
    <div className={styles.root}>
      {isManaged ? (
        <div
          className={mergeClasses(
            styles.banner,
            managedBlockReason === 'missing-ref' ? styles.bannerMissing : styles.bannerConnected,
          )}
        >
          <PlugConnectedRegular
            className={mergeClasses(
              styles.bannerIcon,
              managedBlockReason === 'missing-ref' ? styles.iconMissing : styles.iconConnected,
            )}
          />
          <div className={styles.bannerContent}>
            <Text className={styles.bannerLabel}>
              {managedBlockReason === 'missing-ref' ? 'CONNECTION MISSING' : 'CONNECTION'}
            </Text>
            <Text className={styles.bannerName} title={managedDisplayName || effectiveKey}>
              {managedDisplayName || effectiveKey || '<unknown>'}
            </Text>
          </div>
          <Button
            className={styles.bannerManage}
            appearance="transparent"
            size="small"
            icon={<ChevronRightRegular />}
            iconPosition="after"
            onClick={handleManageConnection}
          >
            Manage
          </Button>
        </div>
      ) : (
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>Server connection</Text>
          <Field>
            <div className={styles.connectionValue}>
              {hasConnection ? connectionLabel : <em>No connection bound</em>}
            </div>
            {connectionEndpoint && <Caption1>{connectionEndpoint}</Caption1>}
          </Field>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={styles.sectionTitle}>Allowed tools</Text>
          {!managedBlockReason && (
            <div className={styles.inlineActions}>
              {allowedTools !== undefined && tools.length > 0 && (
                <Button size="small" appearance="subtle" onClick={handleAllowAll}>
                  Allow all
                </Button>
              )}
              <Button
                size="small"
                appearance="subtle"
                icon={<ArrowClockwiseRegular />}
                onClick={handleRetry}
                disabled={isFetching}
              >
                Refresh
              </Button>
            </div>
          )}
        </div>

        {managedBlockReason === 'missing-ref' && (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Connection missing</MessageBarTitle>
              This tool references a connection that is no longer available. Use
              Manage above to pick a different connection or create a new one.
            </MessageBarBody>
          </MessageBar>
        )}

        {managedBlockReason === 'missing-path' && (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Server path unavailable</MessageBarTitle>
              We couldn't resolve the MCP server path for this tool, so its tool
              list can't be loaded here. The tool will still run at execution
              time using the bound connection.
            </MessageBarBody>
          </MessageBar>
        )}

        {!managedBlockReason && isLoading && (
          <div className={styles.loadingRow} role="status" aria-live="polite">
            <Spinner size="tiny" />
            <Text>Loading tools…</Text>
          </div>
        )}

        {!managedBlockReason && errorMessage && (
          <MessageBar intent={is403 ? 'warning' : 'error'} role="alert">
            <MessageBarBody>
              <MessageBarTitle>
                {is403 ? 'Permission denied' : 'Failed to load tools'}
              </MessageBarTitle>
              {is403
                ? 'You do not have permission to list tools for this MCP server. The tool will still run at execution time using the bound connection.'
                : sanitizeErrorMessage(errorMessage)}
            </MessageBarBody>
          </MessageBar>
        )}

        {!managedBlockReason && !isLoading && !errorMessage && tools.length === 0 && (
          <Text className={styles.emptyState} role="status" aria-live="polite">
            No tools reported by this server.
          </Text>
        )}

        {!managedBlockReason && !isLoading && tools.length > 0 && (
          <div className={styles.toolsList}>
            {tools.map((tool) => (
              <div key={tool.name} className={styles.toolRow}>
                <Checkbox
                  checked={isAllowed(tool.name)}
                  onChange={(_, data) => toggleTool(tool.name, !!data.checked)}
                  label={tool.name}
                />
                {tool.description && (
                  <Caption1 className={styles.toolDescription}>{tool.description}</Caption1>
                )}
              </div>
            ))}
          </div>
        )}

        {!managedBlockReason && allowedTools === undefined && tools.length > 0 && (
          <Caption1>All tools are currently allowed (no allow-list set).</Caption1>
        )}
      </div>
    </div>
  );
};

McpToolConfigTab.displayName = 'McpToolConfigTab';
